'use client';
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Header, useModal, Onboarding, toast } from '@ohif/ui-next';
import { SidebarNavigation } from '@ohif/ui';
import { Types as coreTypes } from '@ohif/core';
import { useAppConfig } from '@state';

// 1. Importaciones del servicio de Pacientes
import {
  fetchAllPatients,
  createPatient,
  updatePatient,
  deletePatient,
  PatientAPI,
  PatientCreateData,
  PatientGender,
} from './pacients'; // Asegúrate de que la ruta sea correcta

// 2. Componentes Separados (Definidos a continuación)
import PatientFormModal from './components/PatientFormModal';
import PatientList from './components/PatienList';
import { getHeaderConfig } from './components/HeaderConfig'; // Mover la config del Header

// --- Estado Inicial del Formulario de Creación/Edición ---
const INITIAL_FORM_DATA: PatientCreateData = {
  patientId: '',
  firstName: '',
  lastName: '',
  gender: 'NO_ESPECIFICADO' as PatientGender,
  dateOfBirth: '', // Formato YYYY-MM-DD
  phoneNumber: '',
  address: '',
  notes: '',
  emergencyContact: {
    name: '',
    phone: '',
  },
};

function PatientManagementPanel({ servicesManager }) {
  const [patients, setPatients] = useState<PatientAPI[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPatient, setEditingPatient] = useState<PatientAPI | null>(null);
  const [formData, setFormData] = useState<PatientCreateData>(INITIAL_FORM_DATA);
  const [isLoading, setIsLoading] = useState(false);

  const { t } = useTranslation();
  const { show } = useModal();
  const navigate = useNavigate();
  const [appConfig] = useAppConfig();

  // Configuración del Header (movida a un archivo separado)
  const { menuOptions } = getHeaderConfig({
    servicesManager,
    t,
    show,
    navigate,
    appConfig,
  });

  // --- Lógica de Seguridad y Carga de Datos ---

  // 1. Verificar Sesión/Token
  useEffect(() => {
    const localStr = localStorage.getItem('usuario');
    const session = localStr ? JSON.parse(localStr) : null;

    if (!session?.token) {
      toast.warning('Hubo un error, no tienes permisos para esta página.');
      navigate('/');
    }
  }, [navigate]);

  // 2. Cargar Pacientes
  const loadPatients = async () => {
    try {
      setIsLoading(true);
      const apiPatients = await fetchAllPatients();
      // No necesitamos mapear tanto, ya que la interfaz PatientAPI es casi directa
      setPatients(apiPatients);
    } catch (error) {
      toast.error('Error al obtener pacientes: ' + (error as any).message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Ya que la verificación de sesión no es asíncrona, llamamos a cargar pacientes directamente
    loadPatients();
  }, []);

  // --- Lógica de Filtro ---
  const filteredPatients = patients.filter(
    patient =>
      patient.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      patient.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      patient.patientId.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // --- Manejadores CRUD ---

  const handleCreatePatient = async () => {
    try {
      const response = await createPatient(formData);
      toast.success(`Paciente ${response.patientId} creado exitosamente.`);
      await loadPatients(); // Recargar la lista
      resetForm();
    } catch (err: any) {
      toast.error('Error al crear paciente: ' + (err.message || err));
    }
  };

  const handleUpdatePatient = async () => {
    if (!editingPatient) {
      return;
    }
    try {
      // Solo enviamos los campos que pueden ser actualizados
      const updateData: Partial<PatientCreateData> = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        gender: formData.gender,
        dateOfBirth: formData.dateOfBirth,
        phoneNumber: formData.phoneNumber,
        address: formData.address,
        notes: formData.notes,
        emergencyContact: formData.emergencyContact,
        // El patientId normalmente no se actualiza
      };

      const response = await updatePatient(editingPatient._id, updateData);
      toast.success(`Paciente ${response.patientId} actualizado exitosamente.`);
      await loadPatients(); // Recargar la lista
      resetForm();
    } catch (err: any) {
      toast.error('Error al actualizar paciente: ' + (err.message || err));
    }
  };

  const handleDeletePatient = async (id: string) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este paciente?')) {
      return;
    }
    try {
      await deletePatient(id);
      toast.success('Paciente eliminado correctamente.');
      setPatients(prev => prev.filter(p => p._id !== id));
    } catch (err: any) {
      toast.error('Error al eliminar el paciente: ' + (err.message || err));
    }
  };

  const handleEditPatient = (patient: PatientAPI) => {
    setEditingPatient(patient);
    // Mapear PatientAPI a PatientCreateData para el formulario
    setFormData({
      patientId: patient.patientId, // No editable, pero útil para mostrar
      firstName: patient.firstName,
      lastName: patient.lastName,
      gender: patient.gender,
      dateOfBirth: patient.dateOfBirth?.split('T')[0] || '', // Asegurar formato YYYY-MM-DD
      phoneNumber: patient.phoneNumber || '',
      address: patient.address || '',
      notes: patient.notes || '',
      emergencyContact: patient.emergencyContact || { name: '', phone: '' },
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData(INITIAL_FORM_DATA);
    setEditingPatient(null);
    setIsDialogOpen(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    editingPatient ? handleUpdatePatient() : handleCreatePatient();
  };

  return (
    <div className="flex h-full flex-col bg-black">
      <Header
        isSticky
        menuOptions={menuOptions}
        isReturnEnabled={false}
        WhiteLabeling={appConfig.whiteLabeling}
      />
      <Onboarding />
      <SidebarNavigation onFilterToggle={() => navigate('/')} />

      <div className="container mx-auto min-h-screen bg-gray-50 pt-5 text-white max-sm:p-4 sm:pl-9">
        {/* Header del Panel */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Gestión de Pacientes</h1>
            <p className="mt-1">Administra los registros de pacientes</p>
          </div>
          <button
            onClick={() => {
              setEditingPatient(null);
              setFormData(INITIAL_FORM_DATA);
              setIsDialogOpen(true);
            }}
            className="bg-secondary-dark hover:bg-primary-dark flex items-center rounded px-4 py-2 text-white"
          >
            Añadir Paciente
          </button>
        </div>

        {/* Lista de Pacientes (Componente Separado) */}
        <PatientList
          patients={filteredPatients}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          isLoading={isLoading}
          handleEditPatient={handleEditPatient}
          handleDeletePatient={handleDeletePatient}
        />
      </div>

      {/* Modal de Formulario (Componente Separado) */}
      <PatientFormModal
        isDialogOpen={isDialogOpen}
        editingPatient={editingPatient}
        formData={formData}
        setFormData={setFormData}
        handleSubmit={handleSubmit}
        resetForm={resetForm}
      />
    </div>
  );
}

PatientManagementPanel.propTypes = {
  servicesManager: PropTypes.object.isRequired,
};

export default PatientManagementPanel;
